import { Button } from '@/components/ui/button'
import * as Card from '@/components/ui/card'
import * as Field from '@/components/ui/field'
import { Heading } from '@/components/ui/heading'
import { Input } from '@/components/ui/input'
import { Text } from '@/components/ui/text'
import { api } from '@api'
import AnimatedBackground from '@components/AnimatedBackground'
import { createForm, valiForm } from '@modular-forms/solid'
import { A } from '@solidjs/router'
import { createSignal } from 'solid-js'
import { css } from 'styled-system/css'
import { Grid } from 'styled-system/jsx'
import { text } from 'styled-system/recipes'
import * as v from 'valibot'

export const ForgotPasswordSchema = v.object({
  email: v.pipe(
    v.string(),
    v.minLength(1, 'Email must be defined'),
    v.regex(/^\S+@\S+\.\S+$/, 'Please enter a valid email'),
  ),
})

export type ForgotPasswordValues = v.InferInput<typeof ForgotPasswordSchema>

export default function ForgotPassword() {
  const [loading, setLoading] = createSignal(false)
  const [errorMsg, setErrorMsg] = createSignal('')
  const [success, setSuccess] = createSignal(false)

  const [form, { Form, Field: FormField }] = createForm<ForgotPasswordValues>({
    validate: valiForm(ForgotPasswordSchema),
    revalidateOn: 'change',
    initialValues: {
      email: '',
    },
  })

  const handleReset = async (values: ForgotPasswordValues) => {
    setLoading(true)
    setErrorMsg('')

    // Make sure to configure your redirect URL in Supabase Dashboard
    const { error } = await api().auth.resetPasswordForEmail(values.email, {
      redirectTo: `${window.location.origin}/auth/update-password`,
    })

    if (error) {
      setErrorMsg(error.message)
    } else {
      setSuccess(true)
    }
    setLoading(false)
  }

  return (
    <main
      class={css({
        display: 'grid',
        alignItems: { base: 'center', mdDown: 'start' },
        pt: { mdDown: 8 },
        justifyContent: 'center',
        height: '100vh',
        width: '100vw',
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate',
        bg: 'white',
      })}
    >
      <AnimatedBackground />
      <Card.Root minW={450}>
        <Card.Header display="flex" justifyContent="center" alignItems="center">
          <img
            class={css({ height: '40px', width: '40px' })}
            src={'/logo.png'}
            alt="sniper circle logo"
          />
          <Heading textStyle={{ base: 'lg' }} lineHeight="35px">
            Reset Password
          </Heading>
        </Card.Header>

        <Card.Body>
          {success() ? (
            <div class={css({ textAlign: 'center', py: 4 })}>
              <Text>Check your email for a password reset link.</Text>
              <Button variant="plain" size="sm" class={css({ mt: 4 })}>
                <A href="/auth/login">Return to Login</A>
              </Button>
            </div>
          ) : (
            <Form onSubmit={handleReset}>
              <Grid gap={4}>
                <Text textStyle="sm" class={css({ mb: 2, textAlign: 'center' })}>
                  Enter your email and we'll send you a link to reset your password.
                </Text>

                <FormField name="email">
                  {(field, formFieldProps) => (
                    <Field.Root invalid={!!field.error}>
                      <Field.Label>Email address</Field.Label>
                      <Input
                        {...formFieldProps}
                        value={field.value ?? ''}
                        type="email"
                        placeholder="Email address"
                        disabled={loading()}
                        autocomplete="email"
                      />
                      <Field.ErrorText>
                        {field.error && <div>{field.error}</div>}
                      </Field.ErrorText>
                    </Field.Root>
                  )}
                </FormField>

                {errorMsg() && (
                  <p
                    class={`${text({ variant: 'danger' })} ${css({ color: 'red', fontSize: 'sm' })}`}
                  >
                    {errorMsg()}
                  </p>
                )}

                <Button
                  type="submit"
                  colorPalette="jade"
                  loading={loading()}
                  disabled={loading()}
                >
                  {loading() ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <Button type="button" variant="plain" size="xs">
                  <A href="/auth/login">
                    <Text textStyle="xs">Back to Login</Text>
                  </A>
                </Button>
              </Grid>
            </Form>
          )}
        </Card.Body>
      </Card.Root>
    </main>
  )
}
